/**
 * JWT exp decode helpers — pure, no dependencies.
 *
 * NOTE: FE-side expiry checks are for UX only (proactive redirect / silent
 * refresh scheduling). BE JwtAuthenticationFilter is the authoritative gate.
 */

/**
 * Decodes the `exp` claim (Unix seconds) from a JWT payload.
 * Returns null on any failure (malformed token, missing exp, decode error).
 */
export function decodeJwtExp(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // Convert base64url → base64
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // Pad to multiple of 4
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }

    const jsonStr = atob(base64);
    const payload = JSON.parse(jsonStr) as Record<string, unknown>;

    const exp = payload['exp'];
    if (typeof exp !== 'number') return null;
    return exp;
  } catch {
    return null;
  }
}

/**
 * Returns true if the token is absent, has no exp, or its exp is in the past
 * (within optional skewMs milliseconds of leeway).
 *
 * Treat decode failure as expired (fail-safe: forces re-auth rather than
 * allowing a potentially invalid token to proceed).
 */
export function isTokenExpired(token: string | null | undefined, skewMs = 0): boolean {
  if (!token) return true;
  const exp = decodeJwtExp(token);
  if (exp === null) return true;
  return exp * 1000 <= Date.now() + skewMs;
}
